import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAngularContext } from 'AngularContext';
import { useRequiredCurrentUser, useRootScope } from 'FrontRoyalAngular';
import { asStatsig } from 'Users';
import { getUserAnalyticsData } from 'EventLogger';
import { statsigClientProvider } from 'StatsigClientProvider';
import { showToast } from 'Toast';
import { type AccountFormValues, type AuthClient } from './editAccount.types';

// Persists name/nickname/email via AuthClient (an auth $http service — not Iguana), then keeps the
// shared currentUser and analytics identity in sync. Mirrors the old edit-account directive's
// updateAccount: success/failure toast, copy the changed fields onto the global currentUser, and
// re-identify. The re-identify is done Iguana-free using the exported asStatsig/getUserAnalyticsData
// helpers rather than the Iguana user's own methods.
export function useUpdateAccount() {
    const { t } = useTranslation('back_royal');
    const $injector = useAngularContext();
    const $rootScope = useRootScope();
    const currentUser = useRequiredCurrentUser();
    const [submitting, setSubmitting] = useState(false);

    const updateAccount = useCallback(
        async (form: AccountFormValues) => {
            const authClient = $injector?.get<AuthClient>('AuthClient');
            if (!authClient) return;

            setSubmitting(true);
            try {
                await authClient.updateAccount(form);

                // The RTK/AuthClient calls don't echo these fields back onto the shared
                // $rootScope.currentUser, so copy them over ourselves — only after success.
                // $applyAsync propagates the writes to Angular watchers; the DS toast is pure
                // React, so it no longer flushes a digest as a side effect.
                const globalUser = $rootScope.currentUser;
                if (globalUser) {
                    globalUser.name = form.name;
                    globalUser.nickname = form.nickname;
                    globalUser.email = form.email;
                    $rootScope.$applyAsync();
                }

                showToast({ message: t('settings.edit_account.account_update_success'), type: 'success' });

                reIdentify({ ...currentUser, name: form.name, nickname: form.nickname, email: form.email });
            } catch (error) {
                const description = (error as { data?: { error_description?: string } })?.data?.error_description;
                showToast({
                    message: description || t('settings.edit_account.account_update_failure'),
                    type: 'danger',
                });
            } finally {
                setSubmitting(false);
            }
        },
        [$injector, $rootScope, currentUser, t],
    );

    return { updateAccount, submitting };
}

// Push updated identity to GTM + Statsig (matches identifyUser, but using the exported helpers so
// we don't depend on the Iguana user object). Fire-and-forget: analytics failures must not surface
// to the user after a successful save.
function reIdentify(user: ReturnType<typeof useRequiredCurrentUser>) {
    getUserAnalyticsData(user)
        .then(data => window.dataLayer?.push(data))
        .catch(() => undefined);
    statsigClientProvider
        .waitForClient({ caller: 'useUpdateAccount' })
        .then(client => client.updateUserSync(asStatsig(user)))
        .catch(() => undefined);
}
